import React, { ReactNode } from "react";
import { AdminFormFieldInput } from "../../components/admin/formFields";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../constants/constants";
import { toast } from "react-toastify";
import { TGeozoneDetailsInitialValues } from "../admin-details/geozone/validation";
import { useSelector } from "react-redux";
import { TMobileHelpers } from "../../api/store/commonSlice";

type GAutocompleteFieldProps = {
  onPlaceChanged: (place: google.maps.places.PlaceResult) => void;
  onFieldClear?: () => void;
  fieldInAdminForm?: boolean;
  AdminFieldOptions?: {
    label: string;
    fieldName: string;
    values: Partial<TGeozoneDetailsInitialValues>;
    handleChange: (event: React.ChangeEvent<any>) => void;
    handleBlur: (event: React.FocusEvent<any>) => void;
    errors: any;
    touched: any;
    userCanEdit: boolean;
    setFieldError?: (field: string, message: string) => void;
  };
  onAddressNotFound?: () => void;
  wrapperClass?: string;
};

const GAutocompleteField: React.FC<GAutocompleteFieldProps> = ({
  onPlaceChanged,
  onFieldClear,
  fieldInAdminForm = false,
  AdminFieldOptions,
  onAddressNotFound,
  wrapperClass = "relative col-span-8 grid grid-cols-12",
}) => {
  const addressInputRef = React.useRef<HTMLInputElement>(null);

  // mobile helpers
  const mobileHelpers = useSelector(
    (state: { commonReducer: { mobileHelpers: TMobileHelpers } }) =>
      state.commonReducer.mobileHelpers
  );

  const [autocompleteInputValue, setAutocompleteInputValue] =
    React.useState("");

  React.useEffect(() => {
    const input = addressInputRef.current;
    if (!input) return;

    // Initialize the autocomplete object
    // @ts-ignore
    // const autocomplete = new google.maps.places.Autocomplete(input, {
    //   types: ["geocode"], // Specify the types of places to return  // ["(cities)"],
    //   componentRestrictions: { country: "us" }, // Optional: restrict to a specific country
    //   fields: ["place_id", "geometry", "name"], // Specify the fields to be returned
    // });
    // // Add a listener for the place_changed event
    // google.maps.event.addListener(autocomplete, "place_changed", function () {
    //   const place = autocomplete.getPlace();
    //   console.log("place", place);
    //   // call the onPlaceChanged callback with the selected place
    //   onPlaceChanged?.(place);
    //   // set the input value to the selected place name
    //   setAutocompleteInputValue(place.name || "");
    // });

    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        // Perform the search or any other action you want
        handleGGeocodeAddress();
      }
    });
  }, []);

  const handleGGeocodeAddress = () => {
    const input = addressInputRef.current;
    if (!input) return;
    const address = input.value;
    // geocode address and set map bounds to the result
    let geocoder: google.maps.Geocoder = new google.maps.Geocoder();
    geocoder
      .geocode({ address } as google.maps.GeocoderRequest)
      .then((result) => {
        const { results } = result;
        console.log(results);
        if (results && results.length > 0) {
          const place = results[0];
          if (addressInputRef.current && place.geometry) {
            addressInputRef.current.value = place.formatted_address || "";
          }
          if (addressInputRef.current) addressInputRef.current.disabled = true;
          // call the onPlaceChanged callback with the selected place
          onPlaceChanged?.(place);
          // set the input value to the selected place name
          setAutocompleteInputValue(place.formatted_address || "");
        }
      })
      .catch((e) => {
        if (addressInputRef.current) addressInputRef.current.disabled = false;
        if (APP_CONFIG.DEBUG.MAPS)
          console.log(
            "Geocode was not successful for the following reason: " + e
          );
        toast.error(
          typeof e === "string"
            ? e
            : "We could not find the place that you are looking for. Please try again."
        );
        if (fieldInAdminForm && AdminFieldOptions?.setFieldError) {
          AdminFieldOptions.setFieldError(
            AdminFieldOptions.fieldName,
            "Address not found. Please try again."
          );
          onAddressNotFound?.();
        }
      });
  };

  if (fieldInAdminForm && !!AdminFieldOptions) {
    return (
      <div className={wrapperClass}>
        <AdminFormFieldInput
          ref={addressInputRef}
          customWrapperClass="col-span-12"
          label={AdminFieldOptions.label}
          type="text"
          id={AdminFieldOptions.fieldName}
          name={AdminFieldOptions.fieldName}
          value={
            (AdminFieldOptions.values as any)?.[AdminFieldOptions.fieldName]
          }
          onChange={(e) => {
            AdminFieldOptions.handleChange?.(e);
          }}
          onBlur={AdminFieldOptions.handleBlur}
          error={AdminFieldOptions.errors?.[AdminFieldOptions.fieldName]}
          touched={AdminFieldOptions.touched?.[AdminFieldOptions.fieldName]}
          disabled={!AdminFieldOptions.userCanEdit}
          detailsFormField={true}
          AsideFieldButtons={
            <>
              {!!addressInputRef.current && addressInputRef.current.value && (
                <button
                  type="button"
                  className="absolute top-1/2 right-12 -translate-y-1/2 p-1.5 rounded-lg hover:bg-black/10 focus:bg-black/10 active:bg-black/10"
                  onClick={() => {
                    if (addressInputRef.current) {
                      // Clear the input value
                      addressInputRef.current.value = "";
                      addressInputRef.current.disabled = false;
                      addressInputRef.current.select();
                      setAutocompleteInputValue("");
                      // call the onFieldClear callback if provided
                      if (onFieldClear) {
                        onFieldClear();
                      }
                    }
                  }}
                  aria-label="clear"
                  title="clear"
                >
                  <img
                    src={svgIcons.CloseIcon}
                    className="size-5 pointer-events-none touch-none"
                    alt="close"
                  />
                </button>
              )}
              <button
                type="button"
                className={`absolute top-1/2 right-2 -translate-y-1/2 p-1 rounded-lg group enabled:bg-accent-blue-pale enabled:hover:bg-accent-blue-dark enabled:focus:bg-accent-blue-dark enabled:active:bg-accent-blue-dark`}
                onClick={handleGGeocodeAddress}
                aria-label="submit"
                title="submit"
              >
                <img
                  src={svgIcons.ExpandIcon}
                  className="size-6 pointer-events-none touch-none translate-x-[1px] group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                  alt="close"
                />
              </button>
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="absolute right-0">
      <img
        src={svgIcons.SearchIcon}
        className="absolute top-1/2 left-3 size-4 -translate-y-1/2 pointer-events-none touch-none z-[1]"
      />
      <AdminFormFieldInput
        ref={addressInputRef}
        label=""
        type="text"
        id="gautocomplete-address"
        name="gautocomplete-address"
        placeholder={
          mobileHelpers.viewportIsMobile ? "Search..." : "Search for address..."
        }
        customWrapperClass="w-64 max-w-[calc(100vw-8rem)] z-[0] rounded-md shadow-md"
        basicInputClass="w-full h-9 rounded-md text-sm ps-9 pe-[78px]"
        innerWrapperClass="relative flex w-full"
      />
      {
        // !!autocompleteInputValue
        true && (
          <>
            <button
              type="button"
              className="absolute top-1/2 right-10 -translate-y-1/2 p-1 rounded-lg group enabled:bg-accent-blue-pale enabled:hover:bg-accent-blue-dark enabled:focus:bg-accent-blue-dark enabled:active:bg-accent-blue-dark"
              onClick={handleGGeocodeAddress}
              aria-label="submit"
              title="submit"
            >
              <img
                src={svgIcons.ExpandIcon}
                className="size-5 pointer-events-none touch-none translate-x-[1px] group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                alt="close"
              />
            </button>
            <button
              type="button"
              className="absolute top-1/2 right-1 -translate-y-1/2 p-1.5 rounded-lg hover:bg-black/10 focus:bg-black/10 active:bg-black/10"
              onClick={() => {
                if (addressInputRef.current) {
                  // Clear the input value
                  addressInputRef.current.value = "";
                  setAutocompleteInputValue("");
                  // call the onFieldClear callback if provided
                  if (onFieldClear) {
                    onFieldClear();
                  }
                }
              }}
              aria-label="clear"
              title="clear"
            >
              <img
                src={svgIcons.CloseIcon}
                className="size-5 pointer-events-none touch-none"
                alt="close"
              />
            </button>
          </>
        )
      }
    </div>
  );
};
export default GAutocompleteField;
